import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Trash2, Search, X, ChevronLeft, ChevronRight, Users, Send, Copy, Link2, CheckCircle, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AmountInput } from '../components/ui/amount-input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { copyText } from '../lib/clipboard';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function Employees() {
  const { t } = useTranslation('employees');
  const { user: currentUser } = useAuth();
  const isReadOnly = currentUser?.role === 'employee';

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({ full_name: '', position: '', status: 'active', salary: '', salary_currency: 'USD', telegram_chat_id: '', email: '' });
  const [botUsername, setBotUsername] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', perPage);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`${API_URL}/api/employees?${params.toString()}`, { withCredentials: true });
      setEmployees(response.data.items || []);
      setTotalItems(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filterStatus, searchQuery, t]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Fetch Telegram bot username for deep links
  useEffect(() => {
    const fetchBotInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/telegram/bot-info`, { withCredentials: true });
        setBotUsername(res.data.username);
      } catch (e) {
        // Bot not configured — that's fine
      }
    };
    fetchBotInfo();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filterStatus, searchQuery, perPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const copyTelegramLink = (employeeId) => {
    if (!botUsername) {
      toast.error(t('telegram.botNotConfigured'));
      return;
    }
    const link = `https://t.me/${botUsername}?start=${employeeId}`;
    copyText(link).then((ok) => {
      if (ok) toast.success(t('telegram.linkCopied'));
      else window.prompt(t('telegram.copyPrompt'), link);  // last resort: select it by hand
    });
  };

  const clearFilters = () => {
    setFilterStatus('all');
    setSearchQuery('');
    setSearchInput('');
  };

  const hasActiveFilters = filterStatus !== 'all' || searchQuery;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        salary: formData.salary !== '' ? parseFloat(formData.salary) : null,
        telegram_chat_id: formData.telegram_chat_id || null,
        email: formData.email || null,
      };
      if (editingEmployee) {
        await axios.patch(`${API_URL}/api/employees/${editingEmployee.employee_id}`, payload, { withCredentials: true });
        toast.success(t('toast.updated'));
      } else {
        await axios.post(`${API_URL}/api/employees`, payload, { withCredentials: true });
        toast.success(t('toast.created'));
      }
      setOpen(false);
      setEditingEmployee(null);
      setFormData({ full_name: '', position: '', status: 'active', salary: '', salary_currency: 'USD', telegram_chat_id: '', email: '' });
      fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      toast.error(t('toast.saveFailed'));
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/employees/${employeeId}`, { withCredentials: true });
      toast.success(t('toast.deleted'));
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      const errorMessage = error.response?.data?.detail || t('toast.deleteFailed');
      toast.error(errorMessage, { duration: 6000 });
    }
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      full_name: employee.full_name,
      position: employee.position,
      status: employee.status,
      salary: employee.salary != null ? employee.salary : '',
      salary_currency: employee.salary_currency || 'USD',
      telegram_chat_id: employee.telegram_chat_id || '',
      email: employee.email || '',
    });
    setOpen(true);
  };

  const openAddDialog = () => {
    setEditingEmployee(null);
    setFormData({ full_name: '', position: '', status: 'active', salary: '', salary_currency: 'USD', telegram_chat_id: '', email: '' });
    setOpen(true);
  };

  const startItem = totalItems === 0 ? 0 : (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, totalItems);

  if (loading && employees.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('subtitle')}
          </p>
        </div>
        {!isReadOnly && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAddDialog} data-testid="add-employee-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('addEmployee')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingEmployee ? t('dialog.editTitle') : t('dialog.newTitle')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="full_name">{t('form.fullName')}</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder={t('form.fullNamePlaceholder')}
                  required
                  data-testid="employee-name-input"
                />
              </div>
              <div>
                <Label htmlFor="position">{t('form.position')}</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  placeholder={t('form.positionPlaceholder')}
                  required
                  data-testid="employee-position-input"
                />
              </div>
              <div>
                <Label htmlFor="email">{t('common:fields.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="employee@example.com"
                  data-testid="employee-email-input"
                />
              </div>
              <div>
                <Label htmlFor="status">{t('common:fields.status')}</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger data-testid="employee-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('common:status.active')}</SelectItem>
                    <SelectItem value="inactive">{t('common:status.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:items-end">
                <div>
                  <Label htmlFor="salary">{t('form.fixedSalary')}</Label>
                  <AmountInput
                    id="salary"
                    value={formData.salary}
                    onChange={(val) => setFormData({ ...formData, salary: val })}
                    placeholder="0.00"
                    data-testid="employee-salary-input"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {t('form.fixedSalaryHint')}
                  </p>
                </div>
                <div>
                  <Label htmlFor="salary_currency">{t('common:fields.currency')}</Label>
                  <Select value={formData.salary_currency} onValueChange={(val) => setFormData({ ...formData, salary_currency: val })}>
                    <SelectTrigger data-testid="employee-salary-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="UZS">UZS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="telegram_chat_id">Telegram</Label>
                {editingEmployee && !formData.telegram_chat_id && botUsername ? (
                  <div className="mt-1 space-y-2">
                    <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <Link2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {t('telegram.sendLinkHint')}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full text-blue-600 border-blue-200 hover:bg-blue-50"
                      onClick={() => copyTelegramLink(editingEmployee.employee_id)}
                    >
                      <Copy className="h-3 w-3 mr-2" />
                      {t('telegram.copyLink')}
                    </Button>
                  </div>
                ) : formData.telegram_chat_id ? (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                      <CheckCircle className="h-3 w-3" />
                      {t('telegram.linkedWith', { id: formData.telegram_chat_id })}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-red-500"
                      onClick={() => setFormData({ ...formData, telegram_chat_id: '' })}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {t('telegram.afterCreateHint')}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="submit-employee-btn">
                  {editingEmployee ? t('common:actions.update') : t('common:actions.create')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {t('common:actions.cancel')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">{t('common:actions.search')}</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('filters.searchPlaceholder')}
                className="pl-9 w-[220px]"
                data-testid="employee-search"
              />
            </div>
          </div>
          <div className="flex items-end">
            <Button type="submit" size="sm" variant="secondary" className="h-10">
              {t('common:actions.search')}
            </Button>
          </div>
        </form>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">{t('common:fields.status')}</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]" data-testid="employee-status-filter">
              <SelectValue placeholder={t('filters.allStatuses')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allStatuses')}</SelectItem>
              <SelectItem value="active">{t('common:status.active')}</SelectItem>
              <SelectItem value="inactive">{t('common:status.inactive')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-10 text-slate-500">
            <X className="h-4 w-4 mr-1" />
            {t('filters.clear')}
          </Button>
        )}
      </div>

      {/* Summary */}
      <div className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <Users className="h-5 w-5 text-emerald-600" />
        <span className="text-sm text-slate-600 dark:text-slate-400">{t('found', { n: totalItems })}</span>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('table.name')}</th>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('table.position')}</th>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.email')}</th>
              {!isReadOnly && <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('table.fixedSalary')}</th>}
              {!isReadOnly && <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">Telegram</th>}
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.status')}</th>
              {!isReadOnly && <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td colSpan={isReadOnly ? 4 : 7} className="px-6 py-12 text-center text-slate-500">
                  {t('empty')}
                </td>
              </tr>
            ) : (
              employees.map((employee) => (
                <tr
                  key={employee.employee_id}
                  className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors duration-150"
                  data-testid={`employee-row-${employee.employee_id}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">
                    <div className="flex items-center gap-3">
                      {employee.avatar ? (
                        <img src={employee.avatar} alt="" className="h-8 w-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                            {employee.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        {employee.full_name}
                        {employee.user_id && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400" title={t('userLinked')}>
                            <Link2 className="h-3 w-3" />
                            {t('userBadge')}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">{employee.position}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">
                    {employee.email ? (
                      <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-400">
                        <Mail className="h-3 w-3" />
                        {employee.email}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">—</span>
                    )}
                  </td>
                  {!isReadOnly && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">
                    {employee.salary != null ? (
                      <span className="font-medium">
                        {employee.salary_currency === 'UZS'
                          ? `${Math.round(employee.salary).toLocaleString()} UZS`
                          : `$${employee.salary.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-500">—</span>
                    )}
                  </td>
                  )}
                  {!isReadOnly && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {employee.telegram_chat_id ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                        <CheckCircle className="h-3 w-3" />
                        {t('telegram.linked')}
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 h-7 px-2"
                        onClick={() => copyTelegramLink(employee.employee_id)}
                        title={t('telegram.copyLink')}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        {t('telegram.copyLinkShort')}
                      </Button>
                    )}
                  </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        employee.status === 'active'
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                      }`}
                    >
                      {['active', 'inactive'].includes(employee.status) ? t(`common:status.${employee.status}`) : employee.status}
                    </span>
                  </td>
                  {!isReadOnly && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(employee)}
                        data-testid={`edit-employee-${employee.employee_id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(employee.employee_id)}
                        data-testid={`delete-employee-${employee.employee_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination Footer */}
        {totalItems > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('pagination.showing', { start: startItem, end: endItem, total: totalItems })}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{t('pagination.rows')}</span>
                <Select value={perPage.toString()} onValueChange={(val) => setPerPage(parseInt(val))}>
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPage(pageNum)}
                      className="w-8 h-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Employees;
